import type { StatusTone } from "@/components/ui";
import type { PaymentStatus } from "./types";

/**
 * Display helpers for Bookings. A booking's time is shown in ITS OWN zone (the
 * venue's, snapshotted at booking) with the zone named, because an operator in
 * another city reading a court time in their own zone is how a no-show dispute
 * starts.
 */

export function formatInZone(iso: string | null | undefined, zone?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleString(undefined, {
      timeZone: zone || undefined,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return d.toLocaleString();
  }
}

/** "Oct 1, 2026, 6:00 PM – 7:00 PM" in the booking's zone. */
export function formatWindow(startIso: string, endIso: string, zone?: string | null): string {
  const start = formatInZone(startIso, zone);
  const end = new Date(endIso);
  if (Number.isNaN(end.getTime())) return start;
  let endText: string;
  try {
    endText = end.toLocaleTimeString(undefined, { timeZone: zone || undefined, hour: "numeric", minute: "2-digit" });
  } catch {
    endText = end.toLocaleTimeString();
  }
  return `${start} – ${endText}`;
}

export function formatMoney(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "PHP" }).format(amount);
  } catch {
    return `${currency ?? ""} ${amount.toFixed(2)}`.trim();
  }
}

export const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  none: "Not paid online",
  pending: "Payment pending",
  paid: "Paid online",
  partially_refunded: "Partly refunded",
  refunded: "Refunded",
  disputed: "Disputed",
  failed: "Payment failed",
};

export function paymentTone(status: PaymentStatus): StatusTone {
  switch (status) {
    case "paid":
      return "success";
    case "pending":
    case "partially_refunded":
      return "warning";
    case "disputed":
    case "failed":
      return "danger";
    case "refunded":
      return "info";
    default:
      return "neutral";
  }
}
